import { createSignalWithValue } from "@/client/utils/signal";
import type { SpliceSearchResult } from "@/shared/splice-search.api";

/**
 * Where a catalog sample can go on the current page, for a tap instead of a
 * drag. A dropzone that accepts dropped samples registers itself here, and
 * the Sounds dock offers a Use action on every row while at least one target
 * is on the page: touch has no drag, and a click is the quicker path on a
 * desktop too.
 *
 * Targets are kept while their element is in the document; a page that
 * rebuilds its dropzone registers the new one and the old one drops out on
 * its own, so nothing has to remember to unregister.
 */
export interface SampleTarget {
    /** The element the target lives in; used for liveness and to scroll it into view. */
    readonly el: HTMLElement;
    /** What to call it when the page has more than one: the dropzone's own title. */
    readonly label: string;
    readonly accept: (result: SpliceSearchResult) => void;
}

const targets = new Set<SampleTarget>();

/**
 * The targets a person could drop on right now: in the document and laid out.
 * Checked at the moment of use, not at registration: a dropzone registers
 * while it is still being built, before its page appends it. The layout check
 * matters on a versioned page, where every release's dropzone exists and only
 * the chosen release's is shown.
 */
export const getSampleTargets = (): readonly SampleTarget[] =>
    [...targets].filter((target) => target.el.isConnected && target.el.getClientRects().length > 0);

/** Every registered target, attached or not; notified on each add or remove. */
export const sampleTargetsSignal = createSignalWithValue<readonly SampleTarget[]>([]);

const publish = (): void => {
    sampleTargetsSignal.notify([...targets]);
};

/** Returns the unregister function; calling it is optional (see above). */
export const registerSampleTarget = (target: SampleTarget): (() => void) => {
    targets.add(target);
    publish();
    return () => {
        targets.delete(target);
        publish();
    };
};

/** Test seam. */
export const resetSampleTargetsForTesting = (): void => {
    targets.clear();
    publish();
};
